/**
 * Shared Form Logic & Components
 * Consolidates Product Wizard and Lazy Form Loader logic.
 * Handles Global Validation and ATC Button Locking.
 */

window.RMIT = window.RMIT || {};

if (typeof SharedFormValidator === 'undefined') {
    window.SharedFormValidator = class SharedFormValidator {
        constructor(rootElement) {
            this.root = rootElement;
            this.form = this.root.closest('form') || this.root.querySelector('form') || this.root;
            // Scope element searches to the containing form so multiple forms on
            // the same page (e.g. product-as-ticket + main registration) do not
            // interfere with each other.
            const scope = (this.form !== this.root) ? this.form : document;
            this.atcButton = scope.querySelector('.Sd_addProduct:not(.Sd_addProductSticky):not(.shared-form-lock-btn)');
            this.actionContainer = scope.querySelector('.pdp-action-container');
            this.overlayId = 'shared-form-lock-overlay';
            this.overlay = null;
            
            this.init();
        }

        init() {
            this.createLockUI();
            // Initial lock
            this.setLockState(false);
            this.lock();

            // Strict submission prevention
            this.form.addEventListener('submit', (e) => {
                if (!this.validateAll()) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    this.triggerHighlight();
                    return false;
                }
            });

            // Block add-to-cart clicks on any button tied to this form while it is
            // invalid. cart-ajax.js adds via a delegated click handler (not a native
            // submit), so the sticky add-to-cart button would otherwise bypass the
            // form lock. Capture phase plus stopImmediatePropagation stops that handler
            // before it runs, without touching the button's disabled state.
            this.clickGuard = (e) => {
                const btn = e.target.closest('.Sd_addProduct');
                if (!btn || btn === this.overlay) return;
                const tiedToForm = (this.form && this.form.contains(btn)) ||
                    (this.form && this.form.id && btn.getAttribute('form') === this.form.id);
                if (!tiedToForm) return;
                if (!this.validateAll()) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    this.triggerHighlight();
                }
            };
            document.addEventListener('click', this.clickGuard, true);
        }

        getAllRequiredInputs() {
            // Find all inputs with required attribute OR checkboxes within a min-selection constrained group
            const required = Array.from(this.root.querySelectorAll('input[required], select[required], textarea[required]'));
            
            // Add inputs from min-selection groups that might not have 'required' attribute
            const minGroups = this.root.querySelectorAll('[data-min-selections] input[type="checkbox"]');
            
            return [...new Set([...required, ...Array.from(minGroups)])];
        }

        validateAll() {
            const inputs = this.getAllRequiredInputs();
            let allValid = true;
            
            inputs.forEach(input => {
                if (!this.checkInput(input)) {
                    allValid = false;
                }
            });

            return allValid;
        }

        checkInput(input) {
            if (input.type === 'checkbox' || input.type === 'radio') {
                const name = input.name;
                // Use this.root (the product-form element) rather than this.form
                // (the Shopify <form>) because the form-drawer teleports the product-form
                // to document.body on open, moving inputs out of the Shopify form scope.
                const group = this.root.querySelectorAll(`input[name="${name}"]`);

                // 1. Min Selections Check (Attribute on Wrapper)
                const wrapper = input.closest('[data-min-selections]');
                if (wrapper && input.type === 'checkbox') {
                    const min = parseInt(wrapper.dataset.minSelections, 10);
                    // Only enforce if min > 0
                    if (min > 0) {
                        const checkedCount = Array.from(group).filter(i => i.checked).length;
                        return checkedCount >= min;
                    }
                }

                // 2. Standard Required Check
                // If the input itself is required, or if we serve as a fallback for a required group
                if (input.hasAttribute('required')) {
                     return Array.from(group).some(i => i.checked);
                }

                return true;
            }
            // Check value existence AND native browser validity (for numbers, emails, patterns)
            return input.value.trim() !== '' && input.checkValidity();
        }

        // Get human-readable error message for an invalid input
        getValidationError(input) {
            if (input.type === 'checkbox') {
                const wrapper = input.closest('[data-min-selections]');
                if (wrapper) {
                    const min = parseInt(wrapper.dataset.minSelections, 10);
                    const name = input.name;
                    const group = this.root.querySelectorAll(`input[name="${name}"]`);
                    const checkedCount = Array.from(group).filter(i => i.checked).length;

                    if (min > 0 && checkedCount < min) {
                        const remaining = min - checkedCount;
                        if (checkedCount === 0) {
                            return `Please select at least ${min} option${min > 1 ? 's' : ''}`;
                        }
                        return `Please select ${remaining} more option${remaining > 1 ? 's' : ''} (${checkedCount}/${min} selected)`;
                    }
                }
                return 'Please select an option';
            }

            if (input.type === 'radio') {
                return 'Please select an option';
            }

            if (input.type === 'number') {
                if (input.value.trim() === '') {
                    return 'Please enter a number';
                }
                if (input.min && parseFloat(input.value) < parseFloat(input.min)) {
                    return `Minimum value is ${input.min}`;
                }
                if (input.max && parseFloat(input.value) > parseFloat(input.max)) {
                    return `Maximum value is ${input.max}`;
                }
                return 'Please enter a valid number';
            }

            if (input.type === 'email') {
                return 'Please enter a valid email address';
            }

            if (input.type === 'date') {
                return 'Please select a date';
            }

            if (input.value.trim() === '') {
                return 'This field is required';
            }

            return 'Please check this field';
        }

        setLockState(isValid) {
            if (isValid) {
                this.unlock();
            } else {
                this.lock();
            }
        }

        createLockUI() {
            // Prefer a container-scoped lookup so multiple forms on the same page
            // each get their own overlay rather than sharing the first one created.
            const existingOverlay = this.actionContainer
                ? this.actionContainer.querySelector('.shared-form-lock-btn')
                : document.getElementById(this.overlayId);
            if (existingOverlay) {
                this.overlay = existingOverlay;
                return;
            }

            if (!this.actionContainer) {
                // Fallback to finding the button's parent if action container not found
                if (this.atcButton) this.actionContainer = this.atcButton.parentElement;
                else return;
            }

            // If a login gate link is already present in the action container
            // (product-as-program shows this for unauthenticated customers),
            // do not create the form-lock overlay. The customer must log in first;
            // showing a second CTA alongside the login button is misleading.
            if (this.actionContainer.querySelector('a[href*="/account/login"]')) return;

            // Create a 'swap' button that sits exactly over the real ATC button
            this.overlay = document.createElement('button');
            this.overlay.id = this.overlayId;
            this.overlay.type = 'button'; // Prevent submission
            this.overlay.className = 'Sd_addProduct shared-form-lock-btn button med-btn'; // Mimic original button class for basic sizing
            
            const lockIcon = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right:8px"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>`;

            this.overlay.innerHTML = `${lockIcon} Complete Form to Add to Cart`;
            
            Object.assign(this.overlay.style, {
                top: '0',
                left: '0',
                width: '100%',
                height: '100%',
                zIndex: '50',
                cursor: 'not-allowed',
                background: 'var(--theme-color, #030f73)', // User requested blue/accent
                color: '#ffffff',
                display: 'none',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
                border: 'none',
                fontWeight: '600',
                fontSize: 'var(--buttonFontSize, 14px)',
                transition: 'all 0.3s ease'
            });
            
            // Click only - no hover effects
            this.overlay.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.triggerHighlight();
            });

            if (getComputedStyle(this.actionContainer).position === 'static') {
                this.actionContainer.style.position = 'relative';
            }
            this.actionContainer.appendChild(this.overlay);
        }

        lock() {
            if (this.atcButton) {
                // Visually hide the real button — do NOT touch disabled.
                // Stock state (disabled/enabled) is owned exclusively by common.js + Liquid.
                this.atcButton.style.visibility = 'hidden';
                this.atcButton.style.display = 'none';
            }
            if (this.overlay) {
                this.overlay.style.display = 'flex';
            }
        }

        unlock() {
            if (this.atcButton) {
                // Restore visibility only — do NOT set disabled = false.
                // common.js fires on every variant change and owns disabled/stock state.
                // If the variant is OOS, the button will correctly remain disabled ("Sold Out")
                // once revealed. If in-stock, common.js will have already removed disabled.
                this.atcButton.style.visibility = 'visible';
                this.atcButton.style.display = 'block';
            }
            if (this.overlay) {
                this.overlay.style.display = 'none';
            }
        }

        triggerHighlight() {
            // Dispatch event so components can highlight their specific invalid fields with smooth animation
            this.root.dispatchEvent(new CustomEvent('nav-highlight-request', { bubbles: true }));
        }
    };
    window.RMIT.SharedFormValidator = window.SharedFormValidator;
}


// ==========================================
// Product Wizard Class
// ==========================================
if (typeof ProductWizard === 'undefined') {
    window.ProductWizard = class ProductWizard extends HTMLElement {
        constructor() {
            super();
            this.currentStep = 0;
            this.steps = [];
            this.progressBar = null;
            this.totalSteps = 0;
            this.validator = null;
        }

        connectedCallback() {
            this.steps = this.querySelectorAll('.wizard-step');
            this.progressBar = this.querySelector('.wizard-progress-fill');
            this.totalSteps = this.steps.length;
            
            this.validator = new SharedFormValidator(this);
            
            this.init();
        }

        init() {
            this.showStep(this.currentStep);
            this.bindEvents();
            
            // Initial check relative to validator
            this.checkGlobalValidation();
        }

        bindEvents() {
            // Navigation buttons
            this.querySelectorAll('[data-action="next"]').forEach(btn => {
                btn.addEventListener('click', () => this.nextStep());
            });
            
            this.querySelectorAll('[data-action="prev"]').forEach(btn => {
                btn.addEventListener('click', () => this.prevStep());
            });
            
            // Number input validation
            this.querySelectorAll('.wizard-input-number').forEach(input => {
                input.addEventListener('keydown', (e) => this.handleNumberInput(e));
                input.addEventListener('input', (e) => {
                    this.validateNumberValue(e.target);
                    this.checkGlobalValidation();
                });
            });

            // Number Increment/Decrement
            this.querySelectorAll('[data-action="increment"]').forEach(btn => {
                    btn.addEventListener('click', (e) => {
                        const input = e.target.closest('.wizard-number-control').querySelector('input');
                        input.value = (parseInt(input.value || 0) + 1);
                        input.classList.remove('error');
                        this.checkGlobalValidation();
                    });
            });

            this.querySelectorAll('[data-action="decrement"]').forEach(btn => {
                    btn.addEventListener('click', (e) => {
                        const input = e.target.closest('.wizard-number-control').querySelector('input');
                        const val = parseInt(input.value || 0);
                        if(val > 0) input.value = val - 1;
                        input.classList.remove('error');
                        this.checkGlobalValidation();
                    });
            });
            
            // Option cards (Single Select)
            this.querySelectorAll('.wizard-option-card:not(.wizard-option-checkbox)').forEach(card => {
                card.addEventListener('click', () => {
                    const value = card.dataset.value;
                    const inputId = card.dataset.inputId;
                    const input = document.getElementById(inputId);
                    if(input) {
                        input.value = value;
                        // Visual formatting + pressed state
                        const parent = card.parentElement;
                        parent.querySelectorAll('.wizard-option-card').forEach(c => {
                            c.classList.remove('selected');
                            c.setAttribute('aria-pressed', 'false');
                        });
                        card.classList.add('selected');
                        card.setAttribute('aria-pressed', 'true');
                        
                        this.checkGlobalValidation();

                        // Auto advance
                        setTimeout(() => this.nextStep(), 800);

                    }
                });
            });

            // Checkboxes & Regular Inputs
            this.querySelectorAll('input:not(.wizard-input-number), select, textarea').forEach(input => {
                
                // Specific Logic for Max Selections (Interaction)
                if (input.type === 'checkbox') {
                    const wrapper = input.closest('[data-max-selections]');
                    if (wrapper) {
                         input.addEventListener('change', () => {
                             const max = parseInt(wrapper.dataset.maxSelections, 10);
                             const name = input.name;
                             // Scope to wizard
                             const group = this.querySelectorAll(`input[name="${name}"]`);
                             const checkedCount = Array.from(group).filter(i => i.checked).length;
                             
                             if (checkedCount >= max) {
                                 group.forEach(cb => {
                                     if (!cb.checked) {
                                         cb.disabled = true;
                                         cb.parentElement.classList.add('disabled');
                                     }
                                 });
                             } else {
                                 group.forEach(cb => {
                                     cb.disabled = false;
                                     cb.parentElement.classList.remove('disabled');
                                 });
                             }
                         });
                    }
                }

                input.addEventListener('change', () => this.checkGlobalValidation());
                input.addEventListener('input', () => this.checkGlobalValidation());
            });

            // Handle Highlight Request from Overlay
            this.addEventListener('nav-highlight-request', () => {
                this.highlightFirstInvalid();
            });
        }

        checkGlobalValidation() {
            const isValid = this.validator.validateAll();
            this.validator.setLockState(isValid);
            
            // Update completion tick on last slide if valid
            if (isValid && this.currentStep === this.totalSteps - 1) {
                 // Optional visual cue
            }
            
            return isValid;
        }

        highlightFirstInvalid() {
            const inputs = this.validator.getAllRequiredInputs();
            for (const input of inputs) {
                if (!this.validator.checkInput(input)) {
                    // Find visible step for this input
                    const step = input.closest('.wizard-step');
                    const stepIndex = Array.from(this.steps).indexOf(step);
                    
                    if (stepIndex !== this.currentStep && stepIndex !== -1) {
                        this.showStep(stepIndex);
                    }
                    
                    // Smooth highlight instead of just shake
                    const container = input.closest('.wizard-input-wrapper') || input;
                    container.animate([
                        { transform: 'translateX(0)' },
                        { transform: 'translateX(-5px)' },
                        { transform: 'translateX(5px)' },
                        { transform: 'translateX(0)' }
                    ], {
                        duration: 400,
                        easing: 'ease-in-out'
                    });
                    
                    // Also add error class for visual color
                    container.classList.add('error');
                    setTimeout(() => container.classList.remove('error'), 1000);
                    
                    return;
                }
            }
        }

        showStep(index) {
            if (index < 0 || index >= this.totalSteps) return;

            // Hide all steps
            this.steps.forEach(step => {
                step.classList.remove('active');
                step.classList.remove('exit-left');
            });

            // Show current
            const step = this.steps[index];
            step.classList.add('active');
            
            // Update bar
            const progress = (index / (this.totalSteps - 1)) * 100;
            this.progressBar.style.width = `${progress}%`;
            this.progressBar.parentElement.setAttribute('aria-valuenow', Math.round(progress));

            // Announce step to screen readers
            const announcement = this.querySelector('[data-wizard-step-announcement]');
            if (announcement) {
                announcement.textContent = `Step ${index + 1} of ${this.totalSteps}`;
            }

            this.currentStep = index;
        }

        nextStep() {
            if (this.validateStep(this.currentStep)) {
                // Animate exit
                this.steps[this.currentStep].classList.add('exit-left');
                
                setTimeout(() => {
                    const nextIndex = this.currentStep + 1;
                    if (nextIndex < this.totalSteps) {
                        this.currentStep = nextIndex;
                        this.showStep(this.currentStep);
                    }
                }, 400); // match CSS animation
            }
        }

        prevStep() {
            if (this.currentStep > 0) {
                this.currentStep--;
                this.showStep(this.currentStep);
            }
        }
        
        handleNumberInput(e) {
            // Allow controls
            if(['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete'].includes(e.key)) return;
            
            // Prevent non-numeric
            if (!/^[0-9]$/.test(e.key)) {
                e.preventDefault();
                this.triggerShake(e.target);
            }
        }
        
        validateNumberValue(input) {
            // Remove non-numeric characters if pasted
            if (/[^0-9]/.test(input.value)) {
                input.value = input.value.replace(/[^0-9]/g, '');
                this.triggerShake(input);
            } else {
                input.classList.remove('error');
            }
        }
        
        triggerShake(element) {
            element.classList.remove('wizard-shake');
            element.classList.add('error');
            // Force reflow
            void element.offsetWidth;
            element.classList.add('wizard-shake');
            setTimeout(() => element.classList.remove('wizard-shake'), 400);
        }

        validateStep(index) {
            const step = this.steps[index];
            const inputs = step.querySelectorAll('input, select, textarea');
            let isValid = true;
            
            inputs.forEach(input => {
                if (input.hasAttribute('required') && !this.validator.checkInput(input)) {
                    isValid = false;
                    this.triggerShake(input.closest('.wizard-input-wrapper') || input);
                }
            });
            
            return isValid;
        }
    };

    if (!customElements.get('product-wizard')) {
        customElements.define('product-wizard', ProductWizard);
    }
    window.RMIT.ProductWizard = window.ProductWizard;
}


// ==========================================
// Lazy Form Loader Class
// ==========================================
if (typeof LazyFormLoader === 'undefined') {
    window.LazyFormLoader = class LazyFormLoader {
        constructor(form) {
            this.form = form;
            this.fields = Array.from(form.querySelectorAll('.lazy-form-field'));
            this.progressFill = form.querySelector('[data-lazy-progress]');
            this.progressText = form.querySelector('[data-lazy-count]');
            this.progressContainer = form.querySelector('.lazy-form-progress');
            this.totalFields = this.fields.length;
            this.filledCount = 0;
            this.touchedFields = new Set(); // Track which fields have been interacted with
            this.completedFields = new Set();
            this.isComplete = false;

            this.validator = new SharedFormValidator(this.form);

            this.init();
        }
        
        init() {
            // Check if lazy reveal features should be enabled (for forms with >3 fields)
            this.lazyEnabled = this.form.hasAttribute('data-lazy-enabled');

            // Capture public title for summary
            const titleEl = this.form.querySelector('.lazy-form-title');
            this.formTitle = titleEl ? titleEl.textContent.trim() : 'Form';

            // Create summary card for minimized state (only for lazy-enabled forms)
            if (this.lazyEnabled) {
                this.createSummaryCard();
            }
            
            this.fields.forEach((field, index) => {
                // Select ALL inputs in this field (important for radio groups)
                // FIXED: Include specific attributes for radios and checkboxes used in liquid
                const inputs = field.querySelectorAll('[data-lazy-field], [data-lazy-radio], [data-lazy-checkbox]');

                // Touch/click on the field container - more reliable on iOS
                field.addEventListener('click', () => this.handleFieldInteraction(field, index));
                field.addEventListener('touchstart', () => this.handleFieldInteraction(field, index), { passive: true });

                inputs.forEach(input => {
                     // Reveal on FOCUS of any input - desktop fallback
                    input.addEventListener('focus', () => this.handleFieldInteraction(field, index));
                    
                    // PREVENT NON-NUMERIC INPUT for number fields
                    if (input.type === 'number') {
                        input.addEventListener('keydown', (e) => {
                            // Allow navigation/delete keys
                            if(['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete', 'Enter'].includes(e.key)) return;
                            // Allow Ctrl/Cmd+A/C/V 
                            if(e.ctrlKey || e.metaKey) return;
                            
                            // Prevent non-numeric
                            if (!/^[0-9.]$/.test(e.key)) {
                                e.preventDefault();
                                // Optional: Quick shake or red flash on the specific input
                                input.classList.add('shake-error');
                                setTimeout(() => input.classList.remove('shake-error'), 300);
                            }
                        });
                    }

                    // Track completion on input/change
                    let debounceTimer = null;
                    const debouncedHandler = () => {
                        if (input.classList.contains('lazy-error')) input.classList.remove('lazy-error'); // Clear error on edit

                        if (debounceTimer) clearTimeout(debounceTimer);
                        debounceTimer = setTimeout(() => {
                            this.handleFieldChange(field, index);
                            // Note: handleFieldChange calls updateProgress which handles validation
                        }, 50);
                    };
                    
                    input.addEventListener('input', debouncedHandler);
                    input.addEventListener('change', debouncedHandler);

                    // Initial check for pre-filled values
                    if ((input.type === 'radio' || input.type === 'checkbox') && input.checked) {
                         this.handleFieldChange(field, index);
                    }

                    // Handling Checkbox Max Selections (Disable others when max reached)
                    if (input.type === 'checkbox') {
                        const wrapper = input.closest('.checkbox-options');
                        if (wrapper && wrapper.hasAttribute('data-max-selections')) {
                            input.addEventListener('change', () => {
                                const max = parseInt(wrapper.getAttribute('data-max-selections'), 10);
                                const groupName = input.name;
                                const group = this.form.querySelectorAll(`input[name="${groupName}"]`);
                                const checkedCount = Array.from(group).filter(cb => cb.checked).length;
                                
                                if (checkedCount >= max) {
                                    group.forEach(cb => {
                                        if (!cb.checked) cb.disabled = true;
                                    });
                                } else {
                                    group.forEach(cb => cb.disabled = false);
                                }
                            });
                        }
                    }
                });
            });
            
            // Handle Highlight Request from Overlay
            this.form.addEventListener('nav-highlight-request', () => {
                 this.highlightInvalidFields();
            });

            // Initial state check - updateProgress handles both UI and validation
            this.updateProgress();
        }
        
        checkGlobalValidation() {
            const isValid = this.validator.validateAll();
            this.validator.setLockState(isValid);
            return isValid;
        }

        highlightInvalidFields() {
             // First clear any existing error messages
             this.clearAllErrorMessages();

             const inputs = this.validator.getAllRequiredInputs();
             let firstInvalid = null;
             let hasHiddenInvalid = false;
             const processedFields = new Set(); // Avoid duplicate messages for same field

             inputs.forEach(input => {
                 if (!this.validator.checkInput(input)) {
                     if (!firstInvalid) firstInvalid = input;

                     const field = input.closest('.lazy-form-field');
                     if (field) {
                         if (field.classList.contains('lazy-hidden')) {
                             hasHiddenInvalid = true;
                         } else {
                             // Smooth pulse animation
                             field.animate([
                                 { transform: 'scale(1)', boxShadow: '0 0 0 rgba(220, 53, 69, 0)' },
                                 { transform: 'scale(1.02)', boxShadow: '0 0 10px rgba(220, 53, 69, 0.4)' },
                                 { transform: 'scale(1)', boxShadow: '0 0 0 rgba(220, 53, 69, 0)' }
                             ], {
                                 duration: 600,
                                 easing: 'ease-in-out'
                             });

                             // Add error styling
                             input.classList.add('lazy-error');
                             input.style.borderColor = '#dc3545';

                             // Show error message (once per field)
                             if (!processedFields.has(field)) {
                                 processedFields.add(field);
                                 const errorMsg = this.validator.getValidationError(input);
                                 this.showFieldError(field, input, errorMsg);
                             }
                         }
                     }
                 }
             });

             if (hasHiddenInvalid) {
                 if (this.progressContainer) {
                     this.progressContainer.classList.add('flash-attention');
                     setTimeout(() => this.progressContainer.classList.remove('flash-attention'), 600);
                     this.progressContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
                 }
                 return;
             }

             if (firstInvalid) {
                 firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
                 firstInvalid.focus({ preventScroll: true });
             }
        }

        showFieldError(field, input, message) {
            // Find the right container for the error message
            // For checkboxes/radios, place after the options wrapper
            // For other inputs, place after the input itself
            let targetElement = input;

            if (input.type === 'checkbox' || input.type === 'radio') {
                const wrapper = input.closest('.checkbox-options, .radio-options, [data-lazy-field-group]');
                if (wrapper) targetElement = wrapper;
            }

            // Create error message element
            const errorEl = document.createElement('div');
            errorEl.className = 'lazy-validation-error';
            errorEl.innerHTML = `
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <span>${message}</span>
            `;

            // Insert after the target element
            targetElement.parentNode.insertBefore(errorEl, targetElement.nextSibling);

            // Animate in
            errorEl.style.opacity = '0';
            errorEl.style.transform = 'translateY(-5px)';
            requestAnimationFrame(() => {
                errorEl.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
                errorEl.style.opacity = '1';
                errorEl.style.transform = 'translateY(0)';
            });
        }

        clearAllErrorMessages() {
            // Remove all error message elements
            this.form.querySelectorAll('.lazy-validation-error').forEach(el => el.remove());

            // Clear error styling from inputs
            this.form.querySelectorAll('.lazy-error').forEach(input => {
                input.classList.remove('lazy-error');
                input.style.borderColor = '';
            });
        }

        clearFieldError(field) {
            // Remove error message from this specific field
            const errorEl = field.querySelector('.lazy-validation-error');
            if (errorEl) {
                errorEl.style.opacity = '0';
                errorEl.style.transform = 'translateY(-5px)';
                setTimeout(() => errorEl.remove(), 300);
            }
        }

        createSummaryCard() {
            if (this.form.querySelector('.lazy-summary-card')) return;

            const card = document.createElement('div');
            card.className = 'lazy-summary-card';
            card.innerHTML = `
                <div class="lazy-summary-content">
                    <div class="lazy-summary-icon">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3">
                            <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                    </div>
                    <div>
                        <div class="lazy-summary-text">Form Status</div>
                        <div class="lazy-summary-details">0 of ${this.totalFields} fields filled</div>
                    </div>
                </div>
                <span class="lazy-toggle-hint">Click to edit</span>
            `;
            // Click anywhere on card to toggle
            card.addEventListener('click', () => this.toggleForm());
            
            // Position: Before completion text if exists, otherwise at end
            const completionMsg = this.form.querySelector('.lazy-completion-text');
            if (completionMsg) {
                this.form.insertBefore(card, completionMsg);
            } else {
                this.form.appendChild(card);
            }
            
            this.summaryCard = card;
        }
        
        toggleForm() {
            if (this.form.classList.contains('lazy-minimized')) {
                this.form.classList.remove('lazy-minimized');
            } else {
                this.form.classList.add('lazy-minimized');
            }
        }
        
        handleFieldInteraction(field, index) {
            // Only do lazy reveal for lazy-enabled forms
            if (!this.lazyEnabled) return;

            // Prevent duplicate reveals if already touched
            if (this.touchedFields.has(index)) return;

            // Mark this field as touched
            this.touchedFields.add(index);

            // Simple rule: if field N is touched, field N+1 should be visible
            // No race conditions - just check and reveal immediately
            // Pass the focused element to maintain scroll position
            const focusedInput = field.querySelector('input, select, textarea');
            this.revealFieldsUpTo(index + 1, focusedInput);
        }
        
        handleFieldChange(field, index) {
            // Determine if the *field* is completed
            // A field typically contains one or more inputs.
            const inputs = Array.from(field.querySelectorAll('input, select, textarea'));
            let isFilled = false;

            // If it's a radio/checkbox group, check if ANY is checked
            const isCheckable = inputs.some(i => i.type === 'radio' || i.type === 'checkbox');
            
            if (isCheckable) {
                isFilled = inputs.some(i => i.checked);
            } else {
                // Text inputs - check if value exists
                // Use default validator check logic for consistency
                isFilled = inputs.every(i => {
                    const hasValue = i.value && i.value.trim().length > 0;
                    // Also check number validity if applicable
                    if (i.type === 'number') {
                         return hasValue && i.checkValidity();
                    }
                    return hasValue;
                });
            }
            
            // Check if all inputs in this field pass validation (not just "filled")
            const fieldIsValid = inputs.every(i => this.validator.checkInput(i));

            if (fieldIsValid) {
                // Clear error styling and message when validation passes
                inputs.forEach(i => {
                    i.classList.remove('lazy-error');
                    i.style.borderColor = '';
                });
                this.clearFieldError(field);
            }

            if (isFilled) {
                if (!this.completedFields.has(index)) {
                    this.completedFields.add(index);
                    this.filledCount++;
                    this.markAsCompleted(field);
                }
            } else {
                if (this.completedFields.has(index)) {
                    this.completedFields.delete(index);
                    this.filledCount--;
                    field.classList.remove('lazy-completed');
                }
            }

            // Always update progress/validation - validation can change even when filled state doesn't
            this.updateProgress();

            // Also ensure next field is revealed (backup for touch devices where focus may not fire)
            // Only for lazy-enabled forms
            if (this.lazyEnabled && !this.touchedFields.has(index)) {
                this.touchedFields.add(index);
                this.revealFieldsUpTo(index + 1);
            }
        }

        markAsCompleted(field) {
            field.classList.add('lazy-completed');
        }
        
        _findScrollContainer(el) {
            var node = el ? el.parentElement : null;
            while (node) {
                if (node.hasAttribute('data-scroll-container')) return node;
                node = node.parentElement;
            }
            return null;
        }

        revealFieldsUpTo(targetIndex, focusedElement = null) {
            // Capture scroll position relative to focused element before DOM changes
            let scrollAnchor = null;
            if (focusedElement) {
                const rect = focusedElement.getBoundingClientRect();
                scrollAnchor = {
                    element: focusedElement,
                    offsetFromTop: rect.top
                };
            }

            // Reveal all fields up to and including targetIndex
            this.fields.forEach((field, i) => {
                if (i <= targetIndex && field.classList.contains('lazy-hidden')) {
                    field.classList.remove('lazy-hidden');
                    field.classList.add('lazy-revealing');

                    // Clean up animation class after it completes
                    setTimeout(() => {
                        field.classList.remove('lazy-revealing');
                    }, 1000);
                }
            });

            // Restore scroll position to keep focused element in same viewport position
            if (scrollAnchor) {
                requestAnimationFrame(() => {
                    const newRect = scrollAnchor.element.getBoundingClientRect();
                    const scrollDiff = newRect.top - scrollAnchor.offsetFromTop;
                    if (Math.abs(scrollDiff) > 5) {
                        const sc = this._findScrollContainer(scrollAnchor.element);
                        if (sc) {
                            sc.scrollTop += scrollDiff;
                        } else {
                            window.scrollBy({ top: scrollDiff, behavior: 'instant' });
                        }
                    }
                });
            }
        }
        
        updateProgress() {
            // Check validation once and use for all state updates
            const isValid = this.validator.validateAll();

            // Update progress UI only for lazy-enabled forms
            if (this.lazyEnabled && this.progressFill) {
                const percentage = Math.round((this.filledCount / this.totalFields) * 100);
                this.progressFill.style.width = `${percentage}%`;

                // Update text inside bar if it exists there, or separate element
                if (this.progressText) {
                    // Modern style: "Progress: X/Y" inside the bar or near it
                    this.progressText.textContent = `Progress: ${this.filledCount}/${this.totalFields}`;

                    // Ensure text color contrasts if inside bar
                    if (percentage > 52) {
                         this.progressText.style.color = '#fff';
                    } else {
                         this.progressText.style.color = 'var(--text-color, #333)';
                    }
                }

                // Update summary card if exists
                if (this.summaryCard) {
                    const summaryText = this.summaryCard.querySelector('.lazy-summary-text');
                    const summaryDetails = this.summaryCard.querySelector('.lazy-summary-details');

                    if (summaryText) {
                        const status = isValid ? 'Complete' : 'Incomplete';
                        summaryText.textContent = `${this.formTitle} ${status}`;
                    }
                    if (summaryDetails) summaryDetails.textContent = `${this.filledCount} of ${this.totalFields} fields filled`;
                }
            }

            // Handle Completion Message State and ATC button lock (always, regardless of lazy mode)
            const completionMsg = this.form.querySelector('.lazy-completion-text');

            if (isValid) {
                 if (completionMsg) completionMsg.style.display = 'block';
                 if (this.lazyEnabled && !this.isComplete) this.showCompletionCelebration();
                 this.validator.setLockState(true);
            } else {
                 if (completionMsg) completionMsg.style.display = 'none';
                 if (this.lazyEnabled && this.isComplete) this.removeCompletionState();
                 this.validator.setLockState(false);
            }
        }
        
        removeCompletionState() {
            this.isComplete = false;
            this.form.classList.remove('lazy-minimized');
            this.progressFill.classList.remove('complete');
            
            // Hide completion msg if it exists
            const completionMsg = this.form.querySelector('.lazy-completion-text');
            if(completionMsg) completionMsg.style.display = 'none';
        }
        
        showCompletionCelebration() {
            this.isComplete = true;
            this.progressFill.classList.add('complete');
            
            // Minimize to card to save space
            setTimeout(() => {
                this.form.classList.add('lazy-minimized');
                const sc = this._findScrollContainer(this.form);
                if (sc) {
                    sc.scrollTop = 0;
                } else {
                    window.scrollTo(0, 0);
                }
            }, 2000);

        }
    };
    window.RMIT.LazyFormLoader = window.LazyFormLoader;
}
// ==========================================
// Form State Persistence
// Saves form field answers to sessionStorage before a login redirect
// so they can be restored when the customer returns to the page after logging in.
// Must be defined before initLazyForms so _restoreFormStateAfterLogin exists
// when initLazyForms runs on DOMContentLoaded or immediately (defer scripts run
// while readyState is 'interactive', which hits the else branch synchronously).
// ==========================================
(function () {
    var KEY_PREFIX = 'pf_state:';

    function storageKey() {
        return KEY_PREFIX + window.location.pathname + window.location.search;
    }

    function collectState() {
        var state = {};
        document.querySelectorAll('product-form[data-lazy-form], product-wizard').forEach(function (root) {
            root.querySelectorAll('input, select, textarea').forEach(function (el) {
                if (!el.name) return;
                if (el.type === 'checkbox' || el.type === 'radio') {
                    if (!state[el.name]) state[el.name] = [];
                    if (el.checked) state[el.name].push(el.value);
                } else {
                    state[el.name] = el.value;
                }
            });
        });
        return state;
    }

    function save() {
        var roots = document.querySelectorAll('product-form[data-lazy-form], product-wizard');
        if (!roots.length) return;
        var state = collectState();
        if (Object.keys(state).length === 0) return;
        try { sessionStorage.setItem(storageKey(), JSON.stringify(state)); } catch (e) {}
    }

    // Called from initLazyForms after all form instances are ready
    window._restoreFormStateAfterLogin = function () {
        var key = storageKey();
        var raw;
        try { raw = sessionStorage.getItem(key); } catch (e) { return; }
        if (!raw) return;
        try { sessionStorage.removeItem(key); } catch (e) {}
        var state;
        try { state = JSON.parse(raw); } catch (e) { return; }
        if (!state || typeof state !== 'object' || Object.keys(state).length === 0) return;

        document.querySelectorAll('product-form[data-lazy-form], product-wizard').forEach(function (root) {
            // Reveal all lazy-hidden fields before restoring so events fire on visible inputs
            root.querySelectorAll('.lazy-form-field.lazy-hidden').forEach(function (field) {
                field.classList.remove('lazy-hidden');
            });

            root.querySelectorAll('input, select, textarea').forEach(function (el) {
                if (!el.name || !(el.name in state)) return;
                var saved = state[el.name];
                if (el.type === 'checkbox' || el.type === 'radio') {
                    el.checked = Array.isArray(saved) && saved.indexOf(el.value) !== -1;
                } else {
                    el.value = saved;
                }
                el.dispatchEvent(new Event('input', { bubbles: true }));
                el.dispatchEvent(new Event('change', { bubbles: true }));
            });

            // Restore wizard option card visual selection state and pressed state
            root.querySelectorAll('.wizard-option-card').forEach(function (card) {
                var inputId = card.dataset.inputId;
                if (!inputId) return;
                var input = document.getElementById(inputId);
                if (!input || !input.value) return;
                if (card.dataset.value === input.value) {
                    var parent = card.parentElement;
                    if (parent) {
                        parent.querySelectorAll('.wizard-option-card').forEach(function (c) {
                            c.classList.remove('selected');
                            c.setAttribute('aria-pressed', 'false');
                        });
                    }
                    card.classList.add('selected');
                    card.setAttribute('aria-pressed', 'true');
                }
            });
        });
    };

    // Save on any login-link navigation, covering both the product-as-program
    // login button and any other /account/login link on the page
    document.addEventListener('click', function (e) {
        var link = e.target.closest('a');
        if (!link) return;
        var href = link.getAttribute('href') || '';
        if (href.indexOf('/account/login') !== -1) save();
    });
}());


// Global Init for Lazy Forms
function initLazyForms() {
    document.querySelectorAll('product-form[data-lazy-form]').forEach(form => {
        if (!form.lazyFormLoader) {
            form.lazyFormLoader = new LazyFormLoader(form);
        }
    });
    window._restoreFormStateAfterLogin();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLazyForms);
} else {
    initLazyForms();
}

document.addEventListener('shopify:section:load', initLazyForms);
